import React, { useState, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Route, Routes } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { AppTopbar } from './AppTopbar';
import { AppMenu } from './AppMenu';
import Dashboard from './pages/Dashboard';
import RawMaterialGroups from './pages/rawmaterialgroup/RawMaterialGroups';
import RawMaterialGroupView from './pages/rawmaterialgroup/RawMaterialGroup';
import RawMaterialMasterView from './pages/rawmaterialmaster/RawMaterialMaster';
import MaterialUsageView from './pages/materialusage/MaterialUsage';
import Styles from './pages/style/Styles';
import StyleView from './pages/style/Style';
import BraidsView from './pages/braid/Braids';
import BraidView from './pages/braid/Braid';
import Costing from './pages/costing/Costing';
import MaterialGroupView from './pages/materialgroup/MaterialGroup';
import ProductHierarchies from './pages/producthierarchy/ProductHierarchies';
import PrimeReact from 'primereact/api';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import 'primereact/resources/primereact.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './assets/layout/layout.scss';
import './App.scss';
import SetupChart from './pages/maintenance/SetupChart';
import { usePermissions } from './PermissionsContext';
import { roleBasedMenuAccess } from './authConfig';

interface MenuItem {
    label: string;
    icon: string;
    to?: string;
    fa?: boolean;
    items?: MenuItem[];
}

interface MenuGroup {
    items: MenuItem[];
}

const menu = [
    {
        items: [
            { label: 'Raw Material Group', icon: 'pi pi-qrcode text-2xl', to: '/rawmaterialgroups', fa: false },
            { label: 'Raw Material Master', icon: 'pi pi-book text-2xl', to: '/rawmaterialmaster', fa: false },
            { label: 'Material Usage', icon: 'pi pi-arrows-alt text-2xl', to: '/materialusage', fa: false },
            { label: 'Material Group', icon: 'pi pi-th-large text-2xl', to: '/materialgroup', fa: false },
            { label: 'Style', icon: 'pi pi-megaphone text-2xl', to: '/styles', fa: false },
            { label: 'Braid', icon: 'pi pi-slack text-2xl rotate-45', to: '/braids', fa: false },
            { label: 'Costing', icon: 'pi pi-money-bill text-2xl', to: '/costing', fa: false },
            { label: 'Product Hierarchy', icon: 'pi pi-sitemap text-2xl', to: '/producthierarchy', fa: false },
            {
                label: 'Table Maintenance',
                icon: 'pi pi-table text-2xl',
                fa: false,
                items: [{ label: 'Setup Charts', icon: 'pi pi-chart', to: '/setupchart' }]
            }
        ]
    }
];

// Menu items always visible
const isVisible = (menuLabel: string, userRoles: string[], tabLabel?: string): boolean => {
    // Admin role has full access
    if (userRoles.includes("Admin")) {
        return true;
    }

    // Check visibility for each role
    return userRoles.some((role) => {
        const rolePermissions = roleBasedMenuAccess[role];
        if (!rolePermissions) return false;

        // Separate parent menu check from tab visibility check
        const hasMenuAccess = rolePermissions.menu.includes("All Menus") || rolePermissions.menu.includes(menuLabel);

        if (!hasMenuAccess) return false;

        // If checking for a parent menu without tab, and parent is visible, return true
        if (!tabLabel) {
            return true;
        }

        // If tabLabel is provided, check if the user has access to that specific tab and allow by default if no specific tabs are defined
        const hasTabAccess = rolePermissions.tabs[menuLabel]?.includes(tabLabel) ?? true;
        return hasTabAccess;
    });
};

const filterMenu = (menuGroups: MenuGroup[], roles: string[]): MenuGroup[] => {
    return menuGroups.map((menuGroup) => ({
        items: menuGroup.items
            .map((item) => {
                if (isVisible(item.label, roles)) {

                    // If the item has children (tabs or nested menus), filter child items based on their own menu permissions
                    if (item.items) {
                        const filteredChildItems = item.items.filter((child) =>
                            isVisible(child.label, roles)
                        );
                        return { ...item, items: filteredChildItems };
                    }
                    return item;
                }

                return null;
            })
            .filter((item): item is MenuItem => item !== null)
    }));
};

const App = () => {
    const [layoutColorMode, setLayoutColorMode] = useState('light')
    const [staticMenuInactive, setStaticMenuInactive] = useState<boolean>(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
    const { roles } = usePermissions();
    const nodeRef = useRef(null);

    PrimeReact.ripple = true;
    PrimeReact.inputStyle = 'outlined';

    let menuClick = false;
    let mobileTopbarMenuClick = false;

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, "body-overflow-hidden");
        } else {
            removeClass(document.body, "body-overflow-hidden");
        }
    }, [mobileMenuActive]);

    const onColorModeChange = (mode: string) => {
        setLayoutColorMode(mode)
    }

    const onWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!menuClick) {
            setMobileMenuActive(false);
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false);
        }

        mobileTopbarMenuClick = false;
        menuClick = false;
    }

    const onToggleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        menuClick = true;

        if (isDesktop()) {
            setStaticMenuInactive((prevState) => !prevState);
        }
        else {
            setMobileMenuActive((prevState) => !prevState);
        }

        event.preventDefault();
    }

    const onSidebarClick = () => {
        menuClick = true;
    }

    const onMobileTopbarMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        mobileTopbarMenuClick = true;

        setMobileTopbarMenuActive((prevState) => !prevState);
        event.preventDefault();
    }

    const onMobileSubTopbarMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        mobileTopbarMenuClick = true;

        event.preventDefault();
    }

    const onMenuItemClick = (event: any) => {
        if (!event.item.items) {
            setMobileMenuActive(false);
        }
    }

    const isDesktop = () => {
        return window.innerWidth >= 992;
    }

    const filteredMenu = filterMenu(menu, roles);

    const addClass = (element: HTMLElement, className: string) => {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    }

    const removeClass = (element: HTMLElement, className: string) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    const wrapperClass = classNames('layout-wrapper', 'layout-static', {
        'layout-static-sidebar-inactive': staticMenuInactive,
        'layout-mobile-sidebar-active': mobileMenuActive,
        'layout-theme-light': layoutColorMode === 'light',
        'layout-theme-dark': layoutColorMode === 'dark'
    });

    return (
        <div className={wrapperClass} onClick={onWrapperClick}>
            <AppTopbar onToggleMenuClick={onToggleMenuClick} layoutColorMode={layoutColorMode} onColorModeChange={onColorModeChange}
                mobileTopbarMenuActive={mobileTopbarMenuActive} onMobileTopbarMenuClick={onMobileTopbarMenuClick} onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick} />

            <AuthenticatedTemplate>
                <div className="layout-sidebar" onClick={onSidebarClick}>
                    <AppMenu model={filteredMenu} onMenuItemClick={onMenuItemClick} layoutColorMode={layoutColorMode} />
                </div>

                <div className="layout-main-container">
                    <div className="layout-main">
                        <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/rawmaterialgroups" element={<RawMaterialGroups />} />
                            <Route path="/rawmaterialgroups/:id" element={<RawMaterialGroupView />} />
                            <Route path="/rawmaterialmaster" element={<RawMaterialMasterView />} />
                            <Route path="/materialusage" element={<MaterialUsageView />} />
                            <Route path="/materialgroup" element={<MaterialGroupView />} />
                            <Route path="/styles" element={<Styles />} />
                            <Route path="/styles/:styleCode" element={<StyleView />} />
                            <Route path="/braids" element={<BraidsView />} />
                            <Route path="/braids/:id" element={<BraidView />} />
                            <Route path="/costing" element={<Costing />} />
                            <Route path="/producthierarchy" element={<ProductHierarchies />} />
                            <Route path="/setupchart" element={<SetupChart />} />
                        </Routes>
                    </div>
                </div>
                {/* <AppFooter layoutColorMode={layoutColorMode} /> */}
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <div className="layout-main-container col-offset-0">
                    <div className="layout-main">
                        <p className="text-2xl text-center font-bold">Please sign-in to use the Bill of Material Editor.</p>
                        {/* <Login /> */}
                    </div>
                    {/* <AppFooter layoutColorMode={layoutColorMode} /> */}
                </div>
            </UnauthenticatedTemplate>

            <CSSTransition nodeRef={nodeRef} classNames="layout-mask" timeout={{ enter: 200, exit: 200 }} in={mobileMenuActive} unmountOnExit>
                <div ref={nodeRef} className="layout-mask p-element-overlay"></div>
            </CSSTransition>
        </div>
    );
}

export default App;
